import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getInitials } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Heart, Share2, ShoppingCart, Package, Truck, Eye, Loader2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [shippingAddress, setShippingAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [buying, setBuying] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/marketplace/products/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProduct(response.data);
      } catch (error) {
        toast.error('Pwodui pa jwenn');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, token, navigate]);

  const handleSave = async () => {
    try {
      if (product.is_saved) {
        await axios.delete(`${API}/marketplace/products/${productId}/save`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/marketplace/products/${productId}/save`, {}, { headers: { Authorization: `Bearer ${token}` } });
      }
      setProduct(prev => ({ ...prev, is_saved: !prev.is_saved }));
    } catch (error) {
      toast.error('Error');
    }
  };

  const handleBuy = async () => {
    if (!shippingAddress || !phone) {
      toast.error('Please fill all required fields');
      return;
    }
    setBuying(true);
    try {
      await axios.post(`${API}/marketplace/orders`, {
        product_id: productId,
        quantity,
        shipping_address: shippingAddress,
        phone
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Order created!');
      setShowBuyModal(false);
      navigate('/marketplace/orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setBuying(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: product.title, url: window.location.href });
      } catch {}
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lyen kopye!');
    }
  };

  const startChat = async () => {
    try {
      const response = await axios.post(`${API}/chats`, {
        participant_ids: [product.seller_id],
        is_group: false
      }, { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/chat/${response.data.id}`);
    } catch (error) {
      toast.error('Error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) return null;

  const isOwner = product.seller_id === user?.id;
  const totalPrice = product.price * quantity;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="product-detail-page">
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={handleSave}>
              <Heart className={`w-5 h-5 ${product.is_saved ? 'fill-primary text-primary' : ''}`} />
            </Button>
            <Button variant="ghost" size="icon" className="rounded-full" onClick={handleShare}>
              <Share2 className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <div className="aspect-square bg-secondary/30 relative">
        {product.images?.length > 0 ? (
          <img src={product.images[currentImage]} alt={product.title} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-24 h-24 text-muted-foreground" />
          </div>
        )}
        {product.images?.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {product.images.map((_, idx) => (
              <button key={idx} onClick={() => setCurrentImage(idx)} className={`w-2 h-2 rounded-full ${currentImage === idx ? 'bg-primary' : 'bg-white/50'}`} />
            ))}
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{product.title}</h1>
            <p className="text-3xl font-bold text-primary mt-2">${product.price.toFixed(2)}</p>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <div className="flex items-center gap-1"><Eye className="w-4 h-4" />{product.views} views</div>
            <div>{product.sold_count} vann</div>
          </div>
        </div>

        {product.description && <p className="text-muted-foreground">{product.description}</p>}

        {product.shipping_info && (
          <div className="flex items-start gap-2 text-sm">
            <Truck className="w-4 h-4 mt-0.5 text-primary" />
            <span>{product.shipping_info}</span>
          </div>
        )}

        <div className="text-sm text-muted-foreground">
          {product.quantity > 0 ? <span className="text-green-500">{product.quantity} disponib</span> : <span className="text-destructive">Pa disponib</span>}
        </div>

        <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-2xl">
          <Link to={`/profile/${product.seller?.username}`} className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={product.seller?.avatar} />
              <AvatarFallback className="bg-primary/10 text-primary">{getInitials(product.seller?.display_name || product.seller?.username)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold">{product.seller?.display_name || product.seller?.username}</p>
              <p className="text-sm text-muted-foreground">@{product.seller?.username}</p>
            </div>
          </Link>
          {!isOwner && (
            <Button variant="outline" size="sm" className="rounded-full" onClick={startChat}>
              <MessageCircle className="w-4 h-4 mr-2" />Message
            </Button>
          )}
        </div>
      </div>

      {!isOwner && product.quantity > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 p-4 bg-background border-t border-border/50 safe-bottom">
          <div className="max-w-4xl mx-auto">
            <Button onClick={() => setShowBuyModal(true)} className="w-full rounded-full h-12 font-bold" data-testid="buy-now-btn">
              <ShoppingCart className="w-5 h-5 mr-2" />Achte - ${product.price.toFixed(2)}
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showBuyModal} onOpenChange={setShowBuyModal}>
        <DialogContent className="sm:max-w-md" data-testid="buy-modal">
          <DialogHeader><DialogTitle>Achte {product.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Kantite</Label>
              <div className="flex items-center gap-4">
                <Button variant="outline" size="icon" className="rounded-full" onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</Button>
                <span className="text-xl font-bold w-8 text-center">{quantity}</span>
                <Button variant="outline" size="icon" className="rounded-full" onClick={() => setQuantity(Math.min(product.quantity, quantity + 1))}>+</Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Shipping Address *</Label>
              <Textarea value={shippingAddress} onChange={(e) => setShippingAddress(e.target.value)} placeholder="Your full address..." className="rounded-2xl resize-none" rows={3} data-testid="shipping-address" />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone number" className="rounded-2xl" data-testid="phone-input" />
            </div>
            <div className="p-4 bg-secondary/30 rounded-2xl">
              <div className="flex justify-between mb-2"><span>Pri ({quantity}x)</span><span>${totalPrice.toFixed(2)}</span></div>
              <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-primary">${totalPrice.toFixed(2)}</span></div>
            </div>
            <Button onClick={handleBuy} disabled={buying} className="w-full rounded-full h-12 font-bold" data-testid="confirm-buy-btn">
              {buying ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Confirm Order'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
